import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { api, formatApiError } from "@/lib/api";

const inputCls = "w-full bg-[#0D0E11] border border-white/10 focus:border-amber-500/60 rounded-lg px-3 py-2.5 text-sm text-[#F3F4F6] outline-none transition-colors";
const labelCls = "block font-mono-tactical text-[10px] tracking-[0.2em] text-[#6B7280] mb-2";

export default function ContentEditor() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (content === null) return <div className="py-16 text-center font-mono-tactical text-xs tracking-[0.3em] text-amber-500 animate-pulse-amber">LOADING COPY…</div>;

  const set = (k) => (e) => setContent({ ...content, [k]: e.target.value });
  const setStat = (i, k) => (e) => {
    const stats = [...content.stats];
    stats[i] = { ...stats[i], [k]: e.target.value };
    setContent({ ...content, stats });
  };
  const setTestimonial = (i, k) => (e) => {
    const testimonials = [...content.testimonials];
    testimonials[i] = { ...testimonials[i], [k]: e.target.value };
    setContent({ ...content, testimonials });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { key, ...payload } = content;
      const { data } = await api.put("/content", payload);
      setContent(data);
      toast.success("Site copy updated — live on the landing page now.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="content-editor">
      <div className="bg-[#151821] border border-white/10 rounded-xl p-6">
        <h3 className="font-display font-bold text-lg text-[#F3F4F6] mb-5">Hero</h3>
        <div className="space-y-4">
          <div>
            <label className={labelCls}>KICKER (SMALL LABEL ABOVE HEADLINE)</label>
            <input value={content.hero_kicker || ""} onChange={set("hero_kicker")} className={inputCls} data-testid="content-hero-kicker" />
          </div>
          <div>
            <label className={labelCls}>HEADLINE LINES (ONE PER LINE)</label>
            <textarea rows={3} value={content.hero_lines || ""} onChange={set("hero_lines")} className={inputCls} data-testid="content-hero-lines" />
          </div>
          <div>
            <label className={labelCls}>SUBHEADLINE</label>
            <textarea rows={3} value={content.hero_sub || ""} onChange={set("hero_sub")} className={inputCls} data-testid="content-hero-sub" />
          </div>
        </div>
      </div>

      <div className="bg-[#151821] border border-white/10 rounded-xl p-6">
        <h3 className="font-display font-bold text-lg text-[#F3F4F6] mb-5">Manifesto & Book</h3>
        <div className="space-y-4">
          <div>
            <label className={labelCls}>MANIFESTO (YOUR WORDS)</label>
            <textarea rows={5} value={content.manifesto || ""} onChange={set("manifesto")} className={inputCls} data-testid="content-manifesto" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>BOOK TITLE</label>
              <input value={content.book_title || ""} onChange={set("book_title")} className={inputCls} data-testid="content-book-title" />
            </div>
            <div>
              <label className={labelCls}>AMAZON URL</label>
              <input value={content.amazon_url || ""} onChange={set("amazon_url")} className={inputCls} data-testid="content-amazon-url" />
            </div>
          </div>
          <div>
            <label className={labelCls}>BOOK BLURB</label>
            <textarea rows={3} value={content.book_blurb || ""} onChange={set("book_blurb")} className={inputCls} data-testid="content-book-blurb" />
          </div>
        </div>
      </div>

      <div className="bg-[#151821] border border-white/10 rounded-xl p-6">
        <h3 className="font-display font-bold text-lg text-[#F3F4F6] mb-5">Impact Stats (Bento Grid)</h3>
        <div className="space-y-3">
          {(content.stats || []).map((s, i) => (
            <div key={i} className="grid grid-cols-[110px_1fr] gap-3">
              <input value={s.value} onChange={setStat(i, "value")} className={inputCls} data-testid={`content-stat-value-${i}`} />
              <input value={s.label} onChange={setStat(i, "label")} className={inputCls} data-testid={`content-stat-label-${i}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[#151821] border border-white/10 rounded-xl p-6">
        <h3 className="font-display font-bold text-lg text-[#F3F4F6] mb-2">Endorsements</h3>
        <p className="text-xs text-[#6B7280] mb-5">Replace these sample quotes with real endorsements as you collect them.</p>
        <div className="space-y-5">
          {(content.testimonials || []).map((t, i) => (
            <div key={i} className="border border-white/10 rounded-lg p-4 space-y-3">
              <textarea rows={2} value={t.quote} onChange={setTestimonial(i, "quote")} className={inputCls} data-testid={`content-testimonial-quote-${i}`} />
              <div className="grid sm:grid-cols-2 gap-3">
                <input value={t.name} onChange={setTestimonial(i, "name")} placeholder="Name / title" className={inputCls} data-testid={`content-testimonial-name-${i}`} />
                <input value={t.role} onChange={setTestimonial(i, "role")} placeholder="Facility / organization" className={inputCls} data-testid={`content-testimonial-role-${i}`} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-[#0D0E11] font-bold px-8 py-3.5 rounded-full transition-colors" data-testid="content-save-btn">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
        {saving ? "Saving…" : "Publish Changes"}
      </button>
    </div>
  );
}
